import type { DsdEditorValueOccurrence } from "@/apis/dsd-editor/route";
import { cn } from "@/lib/utils";
import { memo } from "react";
import { DSD_REVIEW_COPY } from "../copy";
import { useDsdReviewEvidenceStore } from "../evidence/evidence-store";
import { DsdReviewGuideCard } from "../guide-card";
import {
  DsdReviewMatchIndicator,
  DsdReviewTagChip,
} from "../review-indicators";
import { DsdReviewNoSelectionBox } from "./no-selection-box";
import { SELECTED_EVIDENCE_CLASS } from "./review-card-parts";
import { DsdReviewSelectedCellCard } from "./selected-cell-card";
import type {
  DsdReviewNoteMatchItem,
  DsdReviewNoteMatchView,
} from "./note-match-view";

/**
 * 주석·주석매칭 후보 카드 — 본문·주석매칭의 후보 카드에서 검토(원형·배지·사유)를 뺀 모양.
 * 칩·일치 표시 문구는 뷰 모델(note-match-view)이 정한다 — 근거 창 머리글과 같은 값
 */
const NoteMatchCard = ({
  item,
  cellId,
  index,
  onPreviewOccurrence,
  onShowEvidence,
}: {
  item: DsdReviewNoteMatchItem;
  cellId: string;
  index: number;
  onPreviewOccurrence: (occurrence: DsdEditorValueOccurrence | null) => void;
  onShowEvidence: (occurrence: DsdEditorValueOccurrence) => void;
}) => {
  const panelCopy = DSD_REVIEW_COPY.panel;
  const showingEvidence = useDsdReviewEvidenceStore(
    (state) => state.target?.cellId === cellId && state.target.index === index,
  );
  return (
    <div
      role="group"
      aria-label={item.title}
      onMouseEnter={() => onPreviewOccurrence(item.occurrence)}
      onMouseLeave={() => onPreviewOccurrence(null)}
      onFocus={() => onPreviewOccurrence(item.occurrence)}
      onBlur={() => onPreviewOccurrence(null)}
      onClick={() => {
        if (window.getSelection()?.toString()) return;
        onShowEvidence(item.occurrence);
      }}
      className={cn(
        "flex cursor-pointer flex-col gap-2 rounded-[12px] bg-background-base p-4 ring-1 ring-inset",
        showingEvidence ? SELECTED_EVIDENCE_CLASS : "ring-line-normal",
      )}
    >
      <div className="flex items-center gap-2">
        <DsdReviewTagChip label={item.tagLabel} tone="positive" />
        <p className="label-1-normal-semibold min-w-0 flex-1 truncate text-label-normal">
          {item.title}
        </p>
      </div>
      {item.axisLabel && (
        <p className="label-2-regular text-label-alternative">
          {item.axisLabel}
        </p>
      )}
      <div className="flex items-center justify-between gap-2">
        <p className="headline-2-semibold text-label-normal">
          {item.value || panelCopy.emptyValue}
        </p>
        <div className="flex items-center gap-2">
          <DsdReviewMatchIndicator label={item.matchLabel} positive />
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onShowEvidence(item.occurrence);
            }}
            className="rounded-small bg-background-gray-alternative px-2.5 py-1 label-2-medium text-label-normal transition-colors hover:bg-decorate-neutral/12"
          >
            {panelCopy.evidence}
          </button>
        </div>
      </div>
    </div>
  );
};

/** 선택 셀 요약 + 값이 같은 후보 카드. memo 이유는 DsdReviewMatchingPanel 과 같다 */
export const DsdReviewNoteMatchingPanel = memo(
  function DsdReviewNoteMatchingPanel({
    count,
    selected,
    onScrollToCell,
    onPreviewOccurrence,
    onShowEvidence,
  }: {
    count: number;
    selected: DsdReviewNoteMatchView | null;
    onScrollToCell: () => void;
    onPreviewOccurrence: (occurrence: DsdEditorValueOccurrence | null) => void;
    onShowEvidence: (occurrence: DsdEditorValueOccurrence) => void;
  }) {
    return (
      <div className="flex h-full flex-col gap-4 overflow-y-auto overscroll-contain p-4">
        <DsdReviewGuideCard
          mode="note-note-matching"
          count={count}
          variant="embedded"
          collapsed={!!selected}
        />
        {selected ? (
          <>
            <DsdReviewSelectedCellCard
              sectionName={selected.sectionName}
              axisLabel={selected.axisLabel}
              value={selected.value}
              onScrollToCell={onScrollToCell}
            />
            <section className="flex flex-col gap-2">
              {selected.items.map((item, index) => (
                <NoteMatchCard
                  key={item.key}
                  item={item}
                  cellId={selected.cellId}
                  index={index}
                  onPreviewOccurrence={onPreviewOccurrence}
                  onShowEvidence={onShowEvidence}
                />
              ))}
            </section>
          </>
        ) : (
          <DsdReviewNoSelectionBox
            message={
              count > 0 ? undefined : DSD_REVIEW_COPY.noteMatching.noMatches
            }
          />
        )}
      </div>
    );
  },
);
